import { useCallback, useEffect, useRef } from "react";
import { useDialogs } from "./useDialogs";

// Borrado con "Deshacer" sin tocar el backend: patron de borrado
// DIFERIDO. Al borrar, sacamos el item de la UI al instante y mostramos
// un toast con "Deshacer"; el DELETE real al server recien se dispara
// cuando vence la ventana (default 5s). Si el usuario toca Deshacer
// antes, cancelamos el timer y restauramos el item — nunca se borro en
// el server, asi que no hace falta endpoint de restore.
//
// Reemplaza al modal de confirmacion bloqueante para borrados no
// criticos (el undo ES la red de seguridad). Para irreversibles en lote
// (limpiar historial, merge) seguir usando dialogs.confirm.
//
// Al DESMONTAR el componente (navegar a otra pagina, cambiar de
// sucursal) CANCELAMOS los borrados pendientes en vez de ejecutarlos.
// Es la opcion segura: nunca disparamos un DELETE acelerado e
// irreversible "por la espalda". Costo aceptable: si borras e
// inmediatamente navegas, el borrado no se concreta y el item sigue ahi
// (lo volves a borrar si querias). Mejor eso que perder datos sin aviso.

type UndoableDeleteArgs = {
  /** id unico del item — permite varios borrados pendientes a la vez. */
  key: string | number;
  /** Texto del toast, ej '"Café" borrado'. */
  message: string;
  /** Sacar el item de la UI ya (optimista). */
  removeOptimistic: () => void;
  /** Volver a poner el item si el usuario toca Deshacer. */
  restore: () => void;
  /** DELETE real al server (se llama al vencer la ventana). */
  commit: () => Promise<unknown>;
  /** Manejo de error del commit (ej toast/alert). */
  onError?: (e: unknown) => void;
  /** Ventana de undo en ms. Default 5000. */
  delayMs?: number;
};

type Pending = {
  timer: number;
  commit: () => Promise<unknown>;
  onError?: (e: unknown) => void;
};

export function useUndoableDelete() {
  const dialogs = useDialogs();
  const pendingRef = useRef<Map<string | number, Pending>>(new Map());

  // Al desmontar el componente, CANCELAR los borrados pendientes (no
  // ejecutar el commit). Evita un DELETE acelerado e irreversible al
  // navegar/cambiar de sucursal dentro de la ventana, y evita correr
  // onError/refetch sobre un componente desmontado.
  useEffect(() => {
    const pending = pendingRef.current;
    return () => {
      pending.forEach(({ timer }) => window.clearTimeout(timer));
      pending.clear();
    };
  }, []);

  return useCallback(
    (args: UndoableDeleteArgs) => {
      const {
        key,
        message,
        removeOptimistic,
        restore,
        commit,
        onError,
        delayMs = 5000,
      } = args;
      const pending = pendingRef.current;
      // Doble click sobre el mismo item mientras ya hay un borrado
      // pendiente: ignorar el segundo.
      if (pending.has(key)) return;

      removeOptimistic();

      const timer = window.setTimeout(() => {
        pending.delete(key);
        commit().catch((e) => onError?.(e));
      }, delayMs);
      pending.set(key, { timer, commit, onError });

      dialogs.toast({
        message,
        variant: "info",
        durationMs: delayMs,
        action: {
          label: "Deshacer",
          onAction: () => {
            const entry = pending.get(key);
            if (!entry) return; // la ventana ya venció, el commit ya salió
            window.clearTimeout(entry.timer);
            pending.delete(key);
            restore();
          },
        },
      });
    },
    [dialogs],
  );
}
